import {
	formatAsMonth,
	formatAsMonthAndShortYear,
	formatAsYear,
} from "../../../core/helpers/date-formatting-util.js";
import { hasOwnProp } from "../../../core/helpers/object-util.js";
import chartBuilder from "../../../core/charts.js";
import chartDisplaySettings from "../../../core/charts/chartDisplaySettings.js";
import HighchartWidget from "../../../core/widgets/HighchartWidget.js";
import keyFigureValueFormatter from "../../../models/keyFigure/rendering/keyFigureValueFormatter.js";
import responsive from "../../../core/responsive.js";
import session from "../../../core/session.js";

const nextId = (function () {
	let chartId = 0;
	return function () {
		return `kpi-chart-container-${chartId++}`;
	};
})();

/**
 * `keyFigureChartWidget` is a chart used to render keyFigure values.
 *
 * @param {boolean} [spec.showTooltip] - Whether to show information when
 * the mouse hovers the chart.
 * @param {boolean} [spec.showDataLabels] - Whether to show numbers inside
 * the chart.
 */
export default class KeyFigureChartWidget extends HighchartWidget {
	constructor(spec = {}) {
		super(...arguments);

		this._data = [];

		this._chartOptions = {};
		this._chartId = nextId();
		this._showTooltip = spec.showTooltip !== false;
		this._showDataLabels = spec.showDataLabels;

		this.onPointClick = this._events.createEvent();
		this.onPlotbandClick = this._events.createEvent();
		this.onPlotbandHovered = this._events.createEvent();
		this.onPlotbandExited = this._events.createEvent();
	}

	setLegend(legend) {
		this._legend = legend;
	}

	/**
	 * Used to set multiple keyFigures series at once, avoiding
	 * some HighCharts limitations
	 */
	setResult(result) {
		if (result.isEmpty()) {
			return;
		}

		const series = this._createNewSeries(result);

		// When building a line series (percentage keyFigure), highcharts
		// will start plotting at the minimum value of the series. This
		// ensures that the scale of the chart starts at 0, always showing
		// the X axis.
		const fakeAxisNeeded = result
			.getKeyFigures()
			.some((keyFigure) => keyFigure.isPercentage());
		if (fakeAxisNeeded) {
			series.push(this._fakeAxisSeries(result));
		}

		// NB! We have to set the plot bands AFTER we created the series,
		// since it will remove the current series and we will not be able
		// to check what state they had (if they was visible or not).
		this._setPlotbands(result);

		this.setSeriesSet({
			series,
			redraw: true,
		});
	}

	// Function called at launch time can not be overridden
	initialize() {
		this._buildChartOptions();
	}

	_colorForKeyFigure(keyFigure) {
		return keyFigure ? this._getPlotOptions(keyFigure).color : "#ffffff";
	}

	_pointMarker(keyFigure) {
		return {
			symbol: "circle",
			lineWidth: 3,
			lineColor: this._colorForKeyFigure(keyFigure),
			fillColor: "#ffffff",
			radius: 6,
		};
	}

	_mergeOptions(options, defaultOptions) {
		Object.keys(defaultOptions).forEach((key) => {
			if (!hasOwnProp(defaultOptions, key)) {
				return;
			}
			if (options[key] === undefined) {
				options[key] = defaultOptions[key];
			}
		});
	}

	/**
	 * Build series option for a specific keyFigure.
	 *
	 * @param {object} options - Options to build the series
	 * @param {KeyFigure} keyFigure - KeyFigure of the series
	 */
	_buildSeries(options, keyFigure) {
		// We now use a scatter series here because even if it will look exactly the same,
		// more options are available on `scatter` series, including `stickyTracking` which will be used
		// to prevent the visual glitch that will move the tooltip to the wrong point.
		const seriesBuilder = keyFigure.isPercentage()
			? chartBuilder.scatterSeries()
			: chartBuilder.series();

		const plotOptions = {
			color: this._colorForKeyFigure(keyFigure),
			id: keyFigure.getIdentifier(),
			keyFigure: keyFigure.serialize(),
			data: this._data[0],
			dataLabels: { style: { textOutline: "none" } },
			marker: this._pointMarker(keyFigure),
			zIndex: keyFigure.isPercentage() ? 2 : 1, // lines is always on top of bars
		};

		const existingSerie = this._getSeries(plotOptions.id);
		if (existingSerie) {
			plotOptions.visible = existingSerie.visible;
		}

		this._mergeOptions(plotOptions, options);

		return seriesBuilder
			.set(plotOptions)
			.set("yAxis", keyFigure.isPercentage() ? 1 : 0)
			.set("animation", false)
			.build();
	}

	/**
	 * Builds all the chart options
	 */
	_buildChartOptions() {
		const that = this;

		const plotOptions = chartBuilder
			.seriesPlotOptions()
			.set("stickyTracking", false)
			.setSelectablePoints({
				onClick: (point) => {
					this.onPointClick.trigger(point);
					return false;
				},
			})
			.set({
				dataLabels: {
					// a truthy value other than true won't work with HC.
					enabled: this._showDataLabels === true,
					formatter: function () {
						const { point, key } = this;

						// We only display the label if (a) we're on mobile
						// and the point is for the current period, or (b)
						// we have less than 24 periods to display.
						const formattedValue = keyFigureValueFormatter.format(
							point.data,
						);
						if (responsive.isMobileBreakpoint()) {
							if (session.getPeriod().includes(key)) {
								return formattedValue;
							}
							return "";
						}
						if (that._data[0].length > 24) {
							return "";
						}
						return formattedValue;
					},
				},
			});

		this._chartOptions = chartBuilder()
			.set("plotOptions", {
				series: plotOptions.build(),
			})
			.set("xAxis", this._buildXAxisOptions())
			.set("yAxis", [
				chartBuilder
					.yAxis({
						id: "column-axis",
					})
					.build(),
				chartBuilder
					.yAxis({
						id: "line-axis",
					})
					.build(),
			])
			.set({
				tooltip: {
					formatter: function () {
						const formatter = this;
						if (!that._showTooltip) {
							return false;
						}
						return chartBuilder
							.keyFigureTooltipFormatter()
							.apply(formatter, arguments);
					},
				},
			})
			.build();
	}

	_buildDataFor(keyFigureValue, index) {
		const that = this;

		return {
			x: keyFigureValue.getPeriod().getStart(),
			y: keyFigureValue.getValue(),
			period: keyFigureValue.getPeriod(),
			data: keyFigureValue,
			events: {
				mouseOver: function () {
					const highchartObject = this;
					const plotband =
						highchartObject.series.xAxis.plotLinesAndBands[index];

					plotband.svgElem.attr(
						"fill",
						chartDisplaySettings.hoverColor,
					);

					that.onPlotbandHovered.trigger(highchartObject);
				},
				mouseOut: function () {
					const highchartObject = this;
					const plotband =
						highchartObject.series.xAxis.plotLinesAndBands[index];
					plotband.svgElem.attr("fill", plotband.options.color);

					that.onPlotbandExited.trigger(highchartObject);
				},
			},
		};
	}

	_setPlotbands(result) {
		const that = this;

		const periods = result.getPeriods();
		const axis = this._buildXAxisOptions()
			.setPeriods({
				periods,
				selectedPeriod: periods.at(-1),
			})
			.setTickIntervalFromPeriodType()
			.setPlotBandEvents({
				mouseover: function () {
					const highchartObject = this;

					highchartObject.svgElem.attr(
						"fill",
						chartDisplaySettings.hoverColor,
					);

					that.onPlotbandHovered.trigger(highchartObject);
				},
				mouseout: function () {
					const highchartObject = this;

					highchartObject.svgElem.attr(
						"fill",
						highchartObject.options.color,
					);

					that.onPlotbandExited.trigger(highchartObject);
				},
			})
			.build();

		// Set data for plotband click handling
		result
			.getValuesForKeyFigure(result.getFirstKeyFigure())
			.forEach((data, index) => {
				axis.plotBands[index].data = data;
			});

		this.setXAxis([axis]);
	}

	_buildXAxisOptions() {
		return chartBuilder
			.xDateAxis()
			.setDateFormat({
				Month: this._formatAxisMonthLabel(),
				Bimester: this._formatAxisBimesterLabel(),
				Quarter: this._formatAxisQuarterLabel(),
				HalfYear: this._formatAxisHalfYearLabel(),
				Year: this._formatAxisYearLabel(),
			})
			.setSelectablePeriods({
				plotBandClicked: (clickedPlotBand) => {
					this.onPlotbandClick.trigger(clickedPlotBand);
				},
			})
			.set({
				tickPixelInterval: responsive.isMobileBreakpoint() ? 20 : 40,
				labels: {
					staggerLines: 1,
					rotation: responsive.isMobileBreakpoint() ? -90 : 0,
					style: {
						paddingLeft: responsive.isMobileBreakpoint()
							? "50px"
							: "0px",
						paddingTop: responsive.isMobileBreakpoint()
							? "4px"
							: "0px",
					},
					y: 20,
				},
			});
	}

	_getPlotOptions(keyFigure) {
		return keyFigure.getChartOptions();
	}

	_makeFormatter(fun) {
		let index = 0;
		return (currentDate, labelData) => {
			index = labelData.isFirst ? 1 : index + 1;
			const dataLength = this._data[0].length;
			const start = this._data[0][0].period;
			const end = this._data[0][dataLength - 1].period;

			// 60px is the min number of pixels we want to have per label
			let labelLength = 60;

			if (!start.isMonth()) {
				// 80px is roughly the width of "Kvartal n XXXX"
				labelLength = 80;
			}

			const maxNumberOfLabels = this._highchart
				? this._highchart.chartWidth / labelLength
				: dataLength;

			// If we do not have enough space, display one label when possible.

			const length = start.numberOfPeriodsSince(end);
			const mod = Math.ceil(length / maxNumberOfLabels);

			if (mod !== 1 && index % mod !== 1) {
				return "";
			}
			return fun(currentDate, labelData, index);
		};
	}

	_createNewSeries(result) {
		return result
			.getKeyFigures()
			.map((keyFigure) =>
				this._createNewSeriesForValues(
					result.getValuesForKeyFigure(keyFigure),
					keyFigure,
				),
			);
	}

	_createNewSeriesForValues(values, keyFigure) {
		this._data.length = 0;
		this._data.push(values.map(this._buildDataFor.bind(this)));

		const options = this._getPlotOptions(keyFigure);
		options.stickyTracking = false;
		return this._buildSeries(options, keyFigure);
	}

	/**
	 * Build a zero-value series to ensure that the X axis is always
	 * displayed, no matter the values of other series.
	 */
	_fakeAxisSeries(keyFigureResult) {
		return {
			name: "axis",
			type: "scatter",
			dataLabels: {
				enabled: false,
			},
			marker: {
				enabled: false,
			},
			data: keyFigureResult.getPeriods().map((period) => ({
				x: period.getStart(),
				y: 0,
			})),
		};
	}

	_formatAxisMonthLabel() {
		return this._makeFormatter((currentDate, labelData) => {
			if (labelData.isFirst || currentDate.getMonth() === 0) {
				return formatAsMonthAndShortYear(currentDate);
			}
			return formatAsMonth(currentDate);
		});
	}

	_formatAxisBimesterLabel() {
		return this._makeFormatter((currentDate) => {
			const currentBimester = this._findBimesterFor(currentDate);
			return (currentBimester && currentBimester.toSmartString()) || "";
		});
	}

	_formatAxisQuarterLabel() {
		return this._makeFormatter((currentDate) => {
			const currentQuarter = this._findQuarterFor(currentDate);
			return (currentQuarter && currentQuarter.toSmartString()) || "";
		});
	}

	_formatAxisHalfYearLabel() {
		return this._makeFormatter((currentDate) => {
			const currentHalfYear = this._findHalfYearFor(currentDate);
			return (currentHalfYear && currentHalfYear.toSmartString()) || "";
		});
	}

	_formatAxisYearLabel() {
		return this._makeFormatter((currentDate) => formatAsYear(currentDate));
	}

	_findBimesterInYear(year, currentDate) {
		let correspondingBimester;

		year.asBimesters().forEach((bimester) => {
			if (!bimester.includes(currentDate)) {
				return;
			}
			correspondingBimester = bimester;
		});

		return correspondingBimester;
	}

	_findBimesterFor(currentDate) {
		const years = session.getCompany().getActiveYears();

		for (const yearIndex in years) {
			if (hasOwnProp(years, yearIndex)) {
				const year = years[yearIndex];
				const correspondingBimester = this._findBimesterInYear(
					year,
					currentDate,
				);
				if (correspondingBimester) {
					return correspondingBimester;
				}
			}
		}

		return null;
	}

	_findQuarterInYear(year, currentDate) {
		let correspondingQuarter;

		year.asQuarters().forEach((quarter) => {
			if (!quarter.includes(currentDate)) {
				return;
			}
			correspondingQuarter = quarter;
		});

		return correspondingQuarter;
	}

	_findQuarterFor(currentDate) {
		const years = session.getCompany().getActiveYears();

		for (const yearIndex in years) {
			if (hasOwnProp(years, yearIndex)) {
				const year = years[yearIndex];
				const correspondingQuarter = this._findQuarterInYear(
					year,
					currentDate,
				);
				if (correspondingQuarter) {
					return correspondingQuarter;
				}
			}
		}

		return null;
	}

	_findHalfYearInYear(year, currentDate) {
		let correspondingHalfYear;

		year.asHalfYears().forEach((halfYear) => {
			if (!halfYear.includes(currentDate)) {
				return;
			}
			correspondingHalfYear = halfYear;
		});

		return correspondingHalfYear;
	}

	_findHalfYearFor(currentDate) {
		const years = session.getCompany().getActiveYears();

		for (const yearIndex in years) {
			if (hasOwnProp(years, yearIndex)) {
				const year = years[yearIndex];
				const correspondingHalfYear = this._findHalfYearInYear(
					year,
					currentDate,
				);
				if (correspondingHalfYear) {
					return correspondingHalfYear;
				}
			}
		}

		return null;
	}
}
